import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IktatgatoService } from '../iktatgato.service';
import { DataService } from '../../data.service';

/**
 *  HIVATALI KAPUN BEÉRKEZETT KÜLDEMÉNYEK ÉS IGAZOLÁSOK – saját menüpont.
 *
 *  Korábban ez a lista a BKSZ-beállító képernyő alján lakott, a tanúsítvány és a
 *  kapcsolati kódok között. Rossz helyen volt: a beállítást egyszer állítja be a
 *  rendszergazda, a postafiókot viszont NAPONTA nézi az ügyintéző – és nem akar
 *  beállító mezők közt görgetni hozzá. Ezért a lista (és az ütemezés, mert azt
 *  ugyanaz az ember kapcsolja, aki a letöltést figyeli) ide költözött; a BKSZ
 *  képernyőn csak egy mutató maradt.
 *
 *  Amit itt lehet, és a beállítóban nem lehetett:
 *   - KERESÉS a tételek közt (érkeztetési szám, feladó, tárgy, fájlnév, iktatószám),
 *   - ÉRKEZTETÉS utólag – az IGAZOLÁSRA is, nem csak a küldeményre,
 *   - IKTATÁS: az érkeztetett tétel adataival nyitjuk az iktató űrlapot.
 */
@Component({
  selector: 'app-ikt-kuldemenyek',
  templateUrl: './kuldemenyek.component.html',
  styleUrls: ['../iktatgato-layout/iktatgato-layout.component.scss'],
})
export class IktKuldemenyekComponent implements OnInit, OnDestroy {
  bejovo: any = { enabled: false, intervallum_perc: 60, statusz: { fut: false }, utolso_hibak: [] };
  lista: any[] = [];
  szuro = '';
  kereses = '';
  betoltFut = false;
  mentesFut = false;
  muveletFut = '';
  private timer: any = null;
  private keresoTimer: any = null;

  readonly intervallumok = [
    { perc: 15, cim: '15 percenként' }, { perc: 30, cim: '30 percenként' }, { perc: 60, cim: 'óránként' },
    { perc: 180, cim: '3 óránként' }, { perc: 360, cim: '6 óránként' }, { perc: 720, cim: '12 óránként' },
    { perc: 1440, cim: 'naponta' },
  ];

  constructor(private ikt: IktatgatoService, private data: DataService, private router: Router) {}

  async ngOnInit() { await this.betolt(); }

  ngOnDestroy() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (this.keresoTimer) { clearTimeout(this.keresoTimer); this.keresoTimer = null; }
  }

  async betolt() {
    this.betoltFut = true;
    try {
      this.bejovo = await this.ikt.bejovoBeallitas();
      this.lista = await this.ikt.bejovoLista(this.szuro, 300, this.kereses);
      if (this.bejovo?.statusz?.fut) this.figyel();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A lista betöltése nem sikerült.', 'error');
    }
    this.betoltFut = false;
  }

  /** Gépelés közben nem kérdezzük minden betűre a szervert. */
  keresesValtozott() {
    if (this.keresoTimer) clearTimeout(this.keresoTimer);
    this.keresoTimer = setTimeout(() => this.betolt(), 350);
  }

  async mentes() {
    this.mentesFut = true;
    try {
      await this.ikt.bejovoBeallitasMent({
        enabled: !!this.bejovo.enabled,
        intervallum_perc: Number(this.bejovo.intervallum_perc) || 60,
      });
      this.data.sendToast(this.bejovo.enabled
        ? 'Mentve – az automatikus letöltés be van kapcsolva.'
        : 'Mentve – az automatikus letöltés ki van kapcsolva.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    }
    this.mentesFut = false;
  }

  async inditas() {
    try {
      const r = await this.ikt.bejovoSzinkron();
      this.bejovo.statusz = r?.statusz || { fut: true };
      this.figyel();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A letöltés nem indult el.', e?.status === 409 ? 'warning' : 'error');
    }
  }

  async megszakitas() {
    try {
      const r = await this.ikt.bejovoMegszakit();
      this.bejovo.statusz = r?.statusz || this.bejovo.statusz;
      this.data.sendToast('Megszakítás kérve – az épp letöltött adag még eltárolódik és visszaigazolódik.', 'info');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A megszakítás nem sikerült.', 'warning');
    }
  }

  private figyel() {
    if (this.timer) return;
    this.timer = setInterval(async () => {
      try {
        const s = await this.ikt.bejovoStatusz();
        this.bejovo.statusz = s || { fut: false };
        if (!s?.fut) {
          clearInterval(this.timer); this.timer = null;
          if (s?.uzenet) this.data.sendToast(s.uzenet, (s.hibak || []).length ? 'warning' : 'success');
          await this.betolt();
        }
      } catch { /* következő körben újra */ }
    }, 2500);
  }

  megnyit(t: any) { this.data.megnyitFajl(this.ikt.bejovoFajlUrl(t.id), t?.fajlnev || 'Küldemény'); }

  /** Érkeztetés – küldeményre és igazolásra ugyanúgy; a szerver egyszer oszt sorszámot. */
  async erkeztet(t: any): Promise<any> {
    if (t.erkeztetes_id) return t;
    this.muveletFut = t.id;
    try {
      const r = await this.ikt.bejovoErkeztet(t.id);
      t.erkeztetes_id = r.erkeztetes_id;
      t.erkeztetes_ev = r.ev;
      t.erkeztetes_sorszam = r.sorszam;
      t.erkeztetes_targy = r.targy;
      if (!r.mar_volt) this.data.sendToast(`Érkeztetve: ${r.sorszam}/${r.ev}`, 'success');
      return t;
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az érkeztetés nem sikerült.', 'error');
      return null;
    } finally {
      this.muveletFut = '';
    }
  }

  /**  IKTATÁS. Érkeztetés nélkül nincs mihez kötni az iratot, ezért ha még nincs,
   *   előbb érkeztetünk – az ügyintézőnek ez egy gomb. */
  async iktat(t: any) {
    const sor = await this.erkeztet(t);
    if (!sor) return;
    this.router.navigate(['/iktatgato/iktatas'], {
      queryParams: {
        erkeztetes: sor.erkeztetes_id,
        targy: sor.erkeztetes_targy || sor.megjegyzes || sor.doktipus_leiras
          || (sor.fajta === 'igazolas' ? `Kézbesítési igazolás (${sor.igazolas_tipus || 'hivatali kapu'})` : '')
          || sor.fajl_nev || '',
        partner: sor.felado_nev || '',
        adathordozo: 'elektronikus',
        digitalis: 1,
        /*  Az igazolás a KIKÜLDÖTT irathoz tartozik: ha megvan a párja, annak az
         *  ügyiratára visszük (alszámos iktatás), és előzményként ki is jelöljük. */
        ugyirat: sor.fajta === 'igazolas' ? (sor.igazolt_ugyirat || '') : '',
        kapcsolodo_irat: sor.fajta === 'igazolas' ? (sor.igazolt_irat || '') : '',
      },
    });
  }

  erkeztetesre() { this.router.navigateByUrl('/iktatgato/erkeztetes'); }

  feladoTipusNev(t: string) {
    return ({ hivatal: 'hivatal', allampolgar: 'állampolgár', rendszer: 'rendszer' } as any)[t] || '–';
  }
}
